import {useState, useCallback} from '@wordpress/element';
import {getSetting} from '@woocommerce/settings';
import {dispatch} from '@wordpress/data';
import {formatPaymentMethod} from '@ppcp/utils';
import {isProduction, i18n} from "../../utils";
import {useLoadPayPalScript, useProcessPaymentFailure} from "../../hooks";
import {usePaymentsClient, useProcessPayment, useGooglePayButton, usePaymentCallbacks} from "./hooks";
import {TokenizedPaymentMethod, PaymentMethodDescription} from "../../components";

export const GooglePay = (props) => {
    const {
        billing,
        onError,
        onClick,
        onClose,
        onSubmit,
        cartData,
        isExpress = false,
        shippingData,
        emitResponse,
        eventRegistration,
        paymentMethodId,
        activePaymentMethod
    } = props;

    const extendedData = cartData?.extensions?.wc_ppcp?.googlepay || {};
    extendedData.isProduction = isProduction();
    const queryParams = getSetting('paypalQueryParams');
    const data = getSetting('ppcp_googlepay_data');
    const title = data.title;
    const fields = getSetting('defaultFields');
    const [buttonContainer, setButtonContainer] = useState(null);
    const {needsShipping} = shippingData;

    const {
        onPaymentSetup,
        onCheckoutFail
    } = eventRegistration;

    const {
        responseTypes,
        noticeContexts
    } = emitResponse;

    const {createErrorNotice} = dispatch('core/notices');

    // Load PayPal SDK
    const paypal = useLoadPayPalScript(queryParams);

    // Setup payment data processing
    const {
        paymentData,
        setPaymentData,
        clearPaymentData
    } = useProcessPayment({
        title,
        isExpress,
        onSubmit,
        onPaymentSetup,
        responseTypes,
        paymentMethodId,
        activePaymentMethod
    });

    // Setup payment callbacks
    const {
        onPaymentAuthorized,
        onPaymentDataChanged
    } = usePaymentCallbacks({
        paypal,
        billing,
        shipping: shippingData,
        setPaymentData
    });

    // Create payments client
    const {
        paymentsClient,
        googlePayConfig
    } = usePaymentsClient({
        paypal,
        needsShipping: needsShipping && isExpress,
        onPaymentAuthorized,
        onPaymentDataChanged
    });

    // Handle payment failures
    useProcessPaymentFailure({
        onError,
        isExpress,
        event: onCheckoutFail,
        responseTypes,
        messageContext: noticeContexts.PAYMENTS,
        setPaymentData,
        paymentMethodId,
        activePaymentMethod,
    });

    // Handle errors
    const handleError = useCallback((error) => {
        if (onError) {
            onError(error);
        } else {
            createErrorNotice(error?.message ? error.message : error, {
                context: noticeContexts.PAYMENTS
            });
        }
    }, [onError, createErrorNotice, noticeContexts]);

    // Create button container ref callback
    const setButtonContainerRef = useCallback(el => {
        setButtonContainer(el);
    }, []);

    // Render Google Pay button
    useGooglePayButton({
        paypal,
        fields,
        paymentData,
        paymentsClient,
        buttonContainer,
        onClick,
        onClose,
        onError: handleError,
        setPaymentData,
        needsShipping: needsShipping,
        settings: {
            ...extendedData,
            isExpress: isExpress,
            config: googlePayConfig
        }
    });

    // Show payment card if payment data exists (non-express only)
    if (!isExpress && paymentData?.paymentMethodData && paymentData.orderId) {
        const email = paymentData.email || billing?.billingAddress?.email || '';
        const {info} = paymentData.paymentMethodData;
        const card = {
            last4: info.cardDetails,
            brand: info.cardNetwork.toUpperCase()
        }

        return (
            <TokenizedPaymentMethod
                icon={{
                    alt: card.brand,
                    src: data.cardIcons[card.brand.toLowerCase()]
                }}
                description={formatPaymentMethod(data.paymentFormat, card)}
                actions={[
                    {
                        type: 'cancel',
                        label: i18n.cancel,
                        onClick: clearPaymentData
                    }
                ]}
            />
        );
    }

    return (
        <>
            {!isExpress &&
                <PaymentMethodDescription description={data.description}/>
            }
            <div className="wc-ppcp-googlepay__buttons" ref={setButtonContainerRef}></div>
        </>
    );
}

export default GooglePay;